// Die Steckdosen aus dem Homelab-Protokoll (Topic `steckdose.stand`;
// docs/protokoll.md › „Steckdosen über das Protokoll“) in genau die beiden
// Formen, die die Seiten aus den REST-Antworten kennen:
//   - GET /v2-api/zwave/devices  → `ZwaveDevice` (alle Geräte mit quelle=zwave),
//   - GET /v2-api/energy/devices → `EnergyDevice` (Zigbee-Geräte plus die
//     Z-Wave-Geräte mit Leistung oder Zählerstand; dort ist `last_seen` der
//     Messzeitpunkt, siehe mqtt.ZwaveHandler.EnergyDevices).
// Z-Wave und Energie sind dieselben Geräte: ein Stand ergibt beide Sichten.
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/steckdosen-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert
// an (`skaliere("steckdose.stand", …)`: W, kWh, V, A); Gegenstück der Rechnung
// ist internal/protokoll/adapter/steckdosen.go.
//
// Regel, mit der die REST-Antworten exakt wiederkehren (der Adapter sendet
// spiegelbildlich):
//   - Zeiger der Quelle (node_id, switch, watts, kwh, volts, amps, online,
//     kwh_total) und has_switch (omitempty) stehen am Ergebnis nur, wenn der
//     Stand sie trägt -- auch als 0/false.
//   - `last_seen` und `measured_at` führt die REST-Antwort ohne `omitempty`
//     (time.Time): fehlt die Zeit im Stand (Nullzeit), setzt der Browser genau
//     den Text wieder ein, den Go für sie schreibt ("0001-01-01T00:00:00Z").
//   - In `EnergyDevice` stehen `watts` und `kwh_total` ohne omitempty: fehlen
//     sie, sind sie `null`.
//   - Die Id steht nur im Instanznamen („steckdose.<16 Hex>.stand“); Name und
//     Quelle stehen im Stand.

import type { EnergyDevice, ZwaveDevice } from "./types";
import type { SteckdoseStand } from "./protokoll/schema.gen";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

/** Instanzname → Id der Steckdose: „steckdose.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“. Alles andere → undefined. */
export function steckdoseIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "steckdose" || t[2] !== "stand" || !/^[0-9a-f]+$/.test(t[1])) return undefined;
  return t[1];
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → die Nullzeit der REST-Antwort. */
function zeit(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return NULLZEIT;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? NULLZEIT : d.toISOString();
}

/** Setzt `o[k]`, wenn der Stand einen Wert trägt (auch 0/false). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

/** Das Z-Wave-Gerät aus seinem Stand, wie `GET /v2-api/zwave/devices` es führt.
 *  Nur für Stände mit quelle = "zwave" sinnvoll. */
export function zwaveGeraetAusProtokoll(st: SteckdoseStand): ZwaveDevice {
  const o: ZwaveDevice = { name: s(st.name), last_seen: zeit(st.zuletzt), measured_at: zeit(st.gemessen) };
  wenn(o, "node_id", st.knoten);
  wenn(o, "switch", st.schalter);
  wenn(o, "has_switch", st.hat_schalter);
  wenn(o, "watts", st.watt);
  wenn(o, "kwh", st.kwh);
  wenn(o, "volts", st.volt);
  wenn(o, "amps", st.ampere);
  wenn(o, "online", st.online);
  wenn(o, "kwh_total", st.kwh_gesamt);
  return o;
}

/** Das Energie-Gerät aus seinem Stand, wie `GET /v2-api/energy/devices` es
 *  führt: Zigbee-Geräte immer, Z-Wave-Geräte nur mit Leistung oder Zählerstand
 *  (`last_seen` = Messzeitpunkt). Alles andere → undefined. */
export function energieGeraetAusProtokoll(st: SteckdoseStand): EnergyDevice | undefined {
  if (st.quelle === "zigbee") {
    return { device: s(st.name), source: "zigbee", watts: st.watt ?? null, kwh_total: st.kwh_gesamt ?? null, last_seen: zeit(st.zuletzt) };
  }
  if (st.quelle === "zwave" && (st.watt != null || st.kwh_gesamt != null)) {
    return { device: s(st.name), source: "zwave", watts: st.watt ?? null, kwh_total: st.kwh_gesamt ?? null, last_seen: zeit(st.gemessen) };
  }
  return undefined;
}

/** Schlüssel eines Energie-Geräts in der Liste: Quelle und Name (gleicher Name
 *  in beiden Quellen sind zwei Geräte). */
export function energieSchluessel(source: string, device: string): string {
  return source + "\u0000" + device;
}

/** Die Reihenfolge von `GET /v2-api/zwave/devices` (nach name; Go vergleicht
 *  Byte für Byte, hier Codeeinheit für Codeeinheit -- gleich, solange kein Name
 *  Zeichen außerhalb der Grundebene trägt). */
export function vergleicheZwave(a: Pick<ZwaveDevice, "name">, b: Pick<ZwaveDevice, "name">): number {
  if (a.name === b.name) return 0;
  return a.name < b.name ? -1 : 1;
}

/** Die Reihenfolge von `GET /v2-api/energy/devices` (nach device; bei gleichem
 *  Namen offen -- hier Zigbee vor Z-Wave). */
export function vergleicheEnergie(a: Pick<EnergyDevice, "device" | "source">, b: Pick<EnergyDevice, "device" | "source">): number {
  if (a.device !== b.device) return a.device < b.device ? -1 : 1;
  if (a.source !== b.source) return a.source < b.source ? -1 : 1; // "zigbee" < "zwave": Zigbee zuerst
  return 0;
}
